/* @flow */
import { bindActionCreators } from 'redux';
import { changeStatus } from 'groups/reducers/groupQuickSwitcher';
import { connect } from 'react-redux';
import cx from 'classnames';
import Dialog from 'components/Dialog.jsx';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import Groups from 'collections/Groups';
import GroupStore from 'groups/stores/GroupStore';
import Input from 'components/Input.jsx';
import KeyConstants from 'constants/KeyConstants';
import React from 'react';
import ReactDOM from 'react-dom';
import ScrollableArea from 'components/ScrollableArea.jsx';
import TextEntities from 'lib/TextEntities';
import TextWithEntities from 'components/TextWithEntities.jsx';
import type {Status} from 'groups/reducers/groupQuickSwitcher'; // eslint-disable-line

import './styles/GroupQuickSwitcher.scss';

type State = {
  groups: Groups,
  query: string,
  results: Groups,
  selected: ?number,
};

type Props = {
  changeStatus: (status: Status) => mixed,
};

class GroupQuickSwitcher extends React.Component {
  props: Props;
  state: State;

  static contextTypes = {
    router: React.PropTypes.object.isRequired,
  }

  constructor(props: any) {
    super(props);

    const groups = new Groups(
      [].concat(
        GroupStore.getOwnedGroups().models,
        GroupStore.getSubscribedGroups().models,
        GroupStore.getSchoolGroups().models,
      )
    );

    this.state = {
      groups,
      query: '',
      results: groups,
      selected: 0,
    };
  }

  componentDidMount() {
    this.props.changeStatus('open');
    setTimeout(this.refs.input.focus, 0);
    document.addEventListener('keydown', this.onKeyPressed);
  }

  componentWillUnmount() {
    this.props.changeStatus('closed');
    document.removeEventListener('keydown', this.onKeyPressed);
  }

  onKeyPressed = (event: any) => {
    let selected = this.state.selected || 0;

    switch (event.keyCode) {
      case KeyConstants.ENTER:
        event.preventDefault();
        this.gotoIndexResult(this.state.selected || 0);
        break;
      case KeyConstants.DOWN:
      case KeyConstants.TAB:
        event.preventDefault();
        if (selected === this.state.results.length - 1) {
          selected = 0;
        } else {
          selected = selected === null ? 0 : selected + 1;
        }
        break;
      case KeyConstants.UP:
        event.preventDefault();
        if (selected === 0) {
          selected = this.state.results.length - 1;
        } else {
          selected = selected > 0 ? selected - 1 : selected;
        }
        break;
      default:
        break;
    }

    if (selected !== this.state.selected) {
      this.setState({ selected });
      const node = ReactDOM.findDOMNode(this.refs[`result-${selected}`]);
      this.refs.scrollArea.scrollIntoView(node);
    }
  }

  onQueryChange = (event: any) => {
    const query = event.target.value.replace('@', '');
    const groups = query.length > 0 ?
      new Groups(this.state.groups.filter(group => {
        return (
          group.getName().toLowerCase().indexOf(query.toLowerCase()) > -1 ||
          group.getCode().toLowerCase().indexOf(query.toLowerCase()) > -1
        );
      })) : this.state.groups;

    this.setState({
      query,
      results: groups,
      selected: query.length > 0 ? 0 : null,
    });
  }

  onResultsHover = (index: number) => {
    this.setState({ selected: index });
  }

  gotoIndexResult = (index: number) => {
    const group = this.state.results.at(index);
    this.context.router.push(`/classes/${group.getCode()}`);
  }

  renderEmptyResults() {
    return (
      <div className="GroupQuickSwitcher-emptyResults">
        <div className="GroupQuickSwitcher-emptyResults-text">
          <div>No matches found for {this.state.query}.</div>
          <div>Have you spelled it correctly? </div>
        </div>
      </div>
    );
  }

  renderResults() {
    return this.state.results.map((group, index) => {
      return (
        <div
          key={group.id}
          className={cx('GroupQuickSwitcher-class', {
            'GroupQuickSwitcher-class-selected': index === this.state.selected,
          })}
          onMouseOver={() => this.onResultsHover(index)}
          onClick={() => this.gotoIndexResult(index)}
          ref={`result-${index}`}>
          <GroupAvatar
            className="GroupQuickSwitcher-class-avatar"
            group={group}
          />
          <span className="GroupQuickSwitcher-class-name" truncate>
            <TextWithEntities
              text={group.getName()}
              entities={TextEntities.parseEmoji(group.getName())}
            />
          </span>
          {' '}
          <span className="GroupQuickSwitcher-class-code">
            @{group.getCode()}
          </span>
        </div>
      );
    });
  }

  render() {
    return (
      <Dialog eventScreenName="GroupQuickSwitcher" {...this.props} size="small">
        <div className="GroupQuickSwitcher">
          <div className="GroupQuickSwitcher-header">
            <span>Jump to a class</span>
            <span>tab or ↑↓ to navigate</span>
            <span>↵ to select</span>
            <span>esc to dismiss</span>
          </div>

          <div className="GroupQuickSwitcher-search">
            <Input ref="input" onChange={this.onQueryChange} />
          </div>

          <div className="GroupQuickSwitcher-results">
            {
              this.state.results.length > 0 ?
                <ScrollableArea key={this.state.results.length} ref="scrollArea">
                  {this.renderResults()}
                </ScrollableArea> : this.renderEmptyResults()
            }
          </div>
        </div>
      </Dialog>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    changeStatus,
  }, dispatch);
}

// $FlowIssue
export default connect(undefined, mapDispatchToProps)(GroupQuickSwitcher);



// WEBPACK FOOTER //
// ./src/groups/components/GroupQuickSwitcher.jsx